/**
 * Cattery Setup Wizard
 * Step-by-step onboarding for new breeders
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2,
  User,
  MapPin,
  Phone,
  Mail,
  Globe,
  Camera,
  Facebook,
  Instagram,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Upload,
  Award,
  FileText
} from 'lucide-react';
import { Card, Button, Input, Select, Alert } from '../shared/UIComponents';
import { useAuth } from '../../hooks/useAuth';
import { SA_CAT_BREEDS, SA_ASSOCIATIONS } from '../../firebase/firestore';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { cn } from '../../utils/helpers';

// South African provinces
const SA_PROVINCES = [
  'Eastern Cape',
  'Free State',
  'Gauteng',
  'KwaZulu-Natal',
  'Limpopo',
  'Mpumalanga',
  'Northern Cape',
  'North West',
  'Western Cape'
];

const STEPS = [
  { id: 1, title: 'Basic Info', icon: Building2 },
  { id: 2, title: 'Location', icon: MapPin },
  { id: 3, title: 'Contact', icon: Phone },
  { id: 4, title: 'Breeds & Registry', icon: Award },
  { id: 5, title: 'Profile Photo', icon: Camera },
];

export const CatterySetup = () => {
  const { user, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    // Basic Info
    catteryName: '',
    catteryPrefix: '',
    ownerName: user?.displayName || '',
    establishedYear: '',
    description: '',
    
    // Location
    province: '',
    city: '',
    address: '',
    showAddress: false,
    
    // Contact
    phone: '',
    whatsapp: '',
    email: user?.email || '',
    website: '',
    facebook: '',
    instagram: '',
    
    // Breeds & Registry
    breeds: [],
    registrations: [],
    
    // Photos
    logoUrl: '',
    coverPhotoUrl: '',
    
    // Settings
    acceptsEnquiries: true,
    showOnMarketplace: true
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      // Create cattery document
      const catteryRef = await addDoc(collection(db, 'catteries'), {
        ...formData,
        ownerId: user.uid,
        ownerEmail: user.email,
        status: 'active',
        verified: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Update user profile with catteryId
      await updateDoc(doc(db, 'users', user.uid), {
        catteryId: catteryRef.id,
        catteryName: formData.catteryName,
        setupComplete: true,
        updatedAt: serverTimestamp()
      });

      // Update auth context
      if (updateUserProfile) {
        await updateUserProfile({ catteryId: catteryRef.id });
      }

      // Redirect to dashboard
      navigate('/dashboard');
      
    } catch (err) {
      console.error('Error creating cattery:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.catteryName && formData.ownerName;
      case 2:
        return formData.province && formData.city;
      case 3:
        return formData.phone || formData.email;
      case 4:
        return formData.breeds.length > 0;
      case 5:
        return true; // Photo is optional
      default:
        return true;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-feline-50 to-paw-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-display font-bold text-gray-900 mb-2">
            Set Up Your Cattery
          </h1>
          <p className="text-gray-600">
            Let's get your cattery profile ready. This only takes a few minutes.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-between mb-8">
          {STEPS.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = currentStep === step.id;
            const isComplete = currentStep > step.id;
            
            return (
              <div key={step.id} className="flex flex-col items-center">
                <div className={cn(
                  'w-12 h-12 rounded-full flex items-center justify-center transition-all',
                  isActive ? 'bg-feline-500 text-white scale-110' :
                  isComplete ? 'bg-paw-500 text-white' :
                  'bg-gray-200 text-gray-400'
                )}>
                  {isComplete ? (
                    <CheckCircle className="w-6 h-6" />
                  ) : (
                    <StepIcon className="w-6 h-6" />
                  )}
                </div>
                <span className={cn(
                  'text-xs mt-2 font-medium',
                  isActive ? 'text-feline-600' : 'text-gray-400'
                )}>
                  {step.title}
                </span>
                
                {/* Connector line */}
                {index < STEPS.length - 1 && (
                  <div className={cn(
                    'absolute w-full h-0.5 top-6 -z-10',
                    isComplete ? 'bg-paw-500' : 'bg-gray-200'
                  )} style={{ left: '50%', width: 'calc(100% - 3rem)' }} />
                )}
              </div>
            );
          })}
        </div>

        {/* Form Card */}
        <Card className="p-8">
          {error && (
            <Alert type="error" className="mb-6" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <Building2 className="w-12 h-12 text-feline-500 mx-auto mb-2" />
                <h2 className="text-xl font-semibold text-gray-900">Basic Information</h2>
                <p className="text-gray-500">Tell us about your cattery</p>
              </div>

              <Input
                label="Cattery Name *"
                value={formData.catteryName}
                onChange={(e) => updateField('catteryName', e.target.value)}
                placeholder="e.g., Whiskers Pride Cattery"
              />

              <Input
                label="Cattery Prefix (for registrations)"
                value={formData.catteryPrefix}
                onChange={(e) => updateField('catteryPrefix', e.target.value)}
                placeholder="e.g., WP"
                maxLength={5}
              />

              <Input
                label="Your Name *"
                value={formData.ownerName}
                onChange={(e) => updateField('ownerName', e.target.value)}
                placeholder="Full name"
              />

              <Input
                label="Year Established"
                type="number"
                value={formData.establishedYear}
                onChange={(e) => updateField('establishedYear', e.target.value)}
                placeholder="e.g., 2020"
                min="1950"
                max={new Date().getFullYear()}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  About Your Cattery
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-feline-300 focus:border-feline-500 outline-none transition-all"
                  placeholder="Tell potential buyers about your cattery, your experience, and what makes your cats special..."
                />
              </div>
            </div>
          )}

          {/* Step 2: Location */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <MapPin className="w-12 h-12 text-feline-500 mx-auto mb-2" />
                <h2 className="text-xl font-semibold text-gray-900">Location</h2>
                <p className="text-gray-500">Where is your cattery located?</p>
              </div>

              <Select
                label="Province *"
                value={formData.province}
                onChange={(e) => updateField('province', e.target.value)}
                options={SA_PROVINCES.map(p => ({ value: p, label: p }))}
                placeholder="Select province"
              />

              <Input
                label="City/Town *"
                value={formData.city}
                onChange={(e) => updateField('city', e.target.value)}
                placeholder="e.g., Cape Town, Johannesburg"
              />

              <Input
                label="Street Address (optional)"
                value={formData.address}
                onChange={(e) => updateField('address', e.target.value)}
                placeholder="Only shown if you enable it below"
              />

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.showAddress}
                  onChange={(e) => updateField('showAddress', e.target.checked)}
                  className="w-5 h-5 text-feline-500 rounded border-gray-300"
                />
                <span className="text-gray-700">Show full address on my profile</span>
              </label>

              <Alert type="info">
                <p className="text-sm">
                  Your city will always be shown to help buyers find local breeders. 
                  Your full address is only shown if you enable it above.
                </p>
              </Alert>
            </div>
          )}

          {/* Step 3: Contact */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <Phone className="w-12 h-12 text-feline-500 mx-auto mb-2" />
                <h2 className="text-xl font-semibold text-gray-900">Contact Details</h2>
                <p className="text-gray-500">How can buyers reach you?</p>
              </div>

              <Input
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                placeholder="082 123 4567"
                icon={<Phone className="w-5 h-5" />}
              />

              <Input
                label="WhatsApp Number"
                value={formData.whatsapp}
                onChange={(e) => updateField('whatsapp', e.target.value)}
                placeholder="Same as phone or different"
                icon={<Phone className="w-5 h-5" />}
              />

              <Input
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                placeholder="your@email.com"
                icon={<Mail className="w-5 h-5" />}
              />

              <Input
                label="Website (optional)"
                value={formData.website}
                onChange={(e) => updateField('website', e.target.value)}
                placeholder="https://yourcattery.co.za"
                icon={<Globe className="w-5 h-5" />}
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Facebook"
                  value={formData.facebook}
                  onChange={(e) => updateField('facebook', e.target.value)}
                  placeholder="facebook.com/..."
                  icon={<Facebook className="w-5 h-5" />}
                />
                <Input
                  label="Instagram"
                  value={formData.instagram}
                  onChange={(e) => updateField('instagram', e.target.value)}
                  placeholder="@yourhandle"
                  icon={<Instagram className="w-5 h-5" />}
                />
              </div>
            </div>
          )}

          {/* Step 4: Breeds & Registry */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <Award className="w-12 h-12 text-feline-500 mx-auto mb-2" />
                <h2 className="text-xl font-semibold text-gray-900">Breeds & Registry</h2>
                <p className="text-gray-500">What breeds do you specialize in?</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Breeds You Breed *
                </label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border border-gray-200 rounded-xl">
                  {SA_CAT_BREEDS.map(breed => (
                    <label key={breed} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-gray-50 rounded-lg">
                      <input
                        type="checkbox"
                        checked={formData.breeds.includes(breed)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            updateField('breeds', [...formData.breeds, breed]);
                          } else {
                            updateField('breeds', formData.breeds.filter(b => b !== breed));
                          }
                        }}
                        className="w-4 h-4 text-feline-500 rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">{breed}</span>
                    </label>
                  ))}
                </div>
                {formData.breeds.length > 0 && (
                  <p className="text-sm text-feline-600 mt-2">
                    Selected: {formData.breeds.join(', ')}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Registry Memberships
                </label>
                <div className="space-y-2">
                  {SA_ASSOCIATIONS.map(assoc => (
                    <label key={assoc.code} className="flex items-center gap-3 cursor-pointer p-3 border border-gray-200 rounded-xl hover:border-feline-300">
                      <input
                        type="checkbox"
                        checked={formData.registrations.includes(assoc.code)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            updateField('registrations', [...formData.registrations, assoc.code]);
                          } else {
                            updateField('registrations', formData.registrations.filter(r => r !== assoc.code));
                          }
                        }}
                        className="w-5 h-5 text-feline-500 rounded border-gray-300"
                      />
                      <div>
                        <p className="font-medium text-gray-900">{assoc.name}</p>
                        <p className="text-xs text-gray-500">{assoc.code}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Profile Photo */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <Camera className="w-12 h-12 text-feline-500 mx-auto mb-2" />
                <h2 className="text-xl font-semibold text-gray-900">Profile Photos</h2>
                <p className="text-gray-500">Add photos to make your profile stand out</p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {/* Logo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cattery Logo
                  </label>
                  <div className="aspect-square border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors">
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-500">Click to upload</span>
                    <span className="text-xs text-gray-400">PNG, JPG up to 2MB</span>
                  </div>
                </div>

                {/* Cover Photo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cover Photo
                  </label>
                  <div className="aspect-square border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors">
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-500">Click to upload</span>
                    <span className="text-xs text-gray-400">PNG, JPG up to 5MB</span>
                  </div>
                </div>
              </div>

              <Alert type="info">
                <p className="text-sm">
                  You can skip this step and add photos later from your settings.
                </p>
              </Alert>

              {/* Final Settings */}
              <div className="pt-6 border-t border-gray-200">
                <h3 className="font-medium text-gray-900 mb-4">Visibility Settings</h3>
                
                <label className="flex items-center gap-3 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    checked={formData.acceptsEnquiries}
                    onChange={(e) => updateField('acceptsEnquiries', e.target.checked)}
                    className="w-5 h-5 text-feline-500 rounded border-gray-300"
                  />
                  <div>
                    <p className="text-gray-700">Accept enquiries from buyers</p>
                    <p className="text-xs text-gray-500">Buyers can send you messages about your cats</p>
                  </div>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.showOnMarketplace}
                    onChange={(e) => updateField('showOnMarketplace', e.target.checked)}
                    className="w-5 h-5 text-feline-500 rounded border-gray-300"
                  />
                  <div>
                    <p className="text-gray-700">Show on public marketplace</p>
                    <p className="text-xs text-gray-500">Your cattery and cats will be visible to buyers</p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            <Button
              variant="ghost"
              onClick={prevStep}
              disabled={currentStep === 1}
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>

            {currentStep < STEPS.length ? (
              <Button onClick={nextStep} disabled={!canProceed()}>
                Next
                <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                loading={submitting}
                disabled={!canProceed()}
              >
                <CheckCircle className="w-4 h-4" />
                Complete Setup
              </Button>
            )}
          </div>
        </Card>

        {/* Skip for now */}
        <p className="text-center mt-4">
          <button 
            onClick={() => navigate('/dashboard')}
            className="text-sm text-gray-500 hover:text-feline-600"
          >
            Skip for now, I'll complete this later
          </button>
        </p>
      </div>
    </div>
  );
};

export default CatterySetup;
